<template>
  <div class="device-method-column-inputs">
    <ApiDeviceSelector
      :deviceSelected="model.device"
      v-bind="{ isRequired, isReadonly }"
      @update:deviceSelected="setDevice"
    />

    <TransitionExpand v-if="canSelectMethod">
      <!--TODO why was it here? model.method !== undefined-->
      <div v-if="model.device">
        <DecoratorInput iconSize="sm" iconTitle="ballot" label="Method" class="mt-4">
          <div
            v-if="isReadonly"
            class="input--faked input--faked-readonly"
            v-html="model.method == null ? 'No method' : model.method.name"
          />
          <SelectMethodComponent
            v-else
            :method="model.method"
            :configuration="deviceConfiguration"
            :deviceId="model.device.id"
            :isMethodCompatible="isMethodCompatible"
            @update:method="setMethod"
          />
          <div
            v-if="
              isRequiredSingleChannelMode &&
              model.method &&
              (model.method.detector_single_channel === 'All' ||
                model.method.detector_single_channel == null)
            "
            class="device-method-column-inputs__error-wrong-method"
          >
            <IconMaterial title="error" size="18" /> This method can not be used in calibration
            sequence. <br />
            You should select a channel mode for detector. Edit this method or create a new one.
          </div>
        </DecoratorInput>
        <div v-if="showMethodDifferent" class="device-method-column-inputs__warning-block">
          <div>
            <i class="material-icons material-icon--14">info</i>
            Device method is different
          </div>
          <Btn
            type="transparent"
            height="s"
            icon-left="keyboard_arrow_up"
            @click="setMethod(model.device.method)"
          >
            use device method
          </Btn>
        </div>
      </div>
    </TransitionExpand>
    <TransitionExpand>
      <!-- remove? model.column !== undefined-->
      <div v-if="model.device && model.column !== undefined">
        <DecoratorInput
          v-if="!hideColumn"
          iconSize="sm"
          iconTitle="hourglass_empty"
          label="Column"
          class="mt-4"
        >
          <div
            v-if="isReadonly"
            class="input--faked input--faked-readonly"
            v-html="model.column == null ? 'No column' : model.column.name"
          />
          <select-column-component v-else :column="model.column" @change="setColumn" />
        </DecoratorInput>
        <div
          v-if="!hideColumn && showColumnDifferent"
          class="device-method-column-inputs__warning-block"
        >
          <div>
            <i class="material-icons material-icon--14">info</i>
            Device column is different
          </div>
          <Btn
            type="transparent"
            height="s"
            icon-left="keyboard_arrow_up"
            @click="setColumn(model.device.column)"
          >
            use device column
          </Btn>
        </div>
      </div>
    </TransitionExpand>

    <SetupPostprocessings
      v-if="
        model.device && canSelectPostprocessing && model.postprocessings && detectorPortDescription
      "
      :postprocessings="model.postprocessings"
      :availableChannels="availableChannels"
      :detectorPortDescription="detectorPortDescription"
      :detectorConfiguration="detectorConfiguration"
      :isRequired="isRequiredPostprocessing"
      :isPreselectMode="!isCalibrationSequenceMode"
      :isCalibrationSequenceMode="isCalibrationSequenceMode"
      class="mt-4"
      @model="setPostprocessings"
    >
      <template #note="{ availableChannels }">
        <div
          v-if="availableChannels.length === 1 && !isRequiredPostprocessing"
          class="device-method-column-inputs__note"
        >
          The selected method supports only one channel. Choose a different one to add multiple
          postprocessings
        </div>
      </template>

      <template #noAvailableChannelsError>
        <span class="device-method-column-inputs__message-no-channel"
          >You must select a method with a specific channel mode for detector</span
        >
      </template>
    </SetupPostprocessings>
    <div
      v-if="model.device && canSelectPostprocessing && !detectorPortDescription"
      class="device-method-column-inputs__error-no-detector mt-4"
    >
      <IconMaterial title="error" size="18" />
      Detector is not enabled. Cannot start injection.
    </div>
  </div>
</template>

<script>
  import SelectMethodComponent from 'components/element/SelectMethodComponent.vue';
  import SelectColumnComponent from 'components/element/SelectColumnComponent.vue';
  import DecoratorInput from '@/uikitProject/decorators/DecoratorInput';
  import ApiDeviceSelector from '@/containers/devices/ApiDeviceSelector';
  import Btn from '@/uikitBase/btns/Btn';
  import TransitionExpand from '@/uikitProject/transitions/TransitionExpand';
  import { apiMethods } from '@/api/graphql/cloud/methods';
  import { canUseMethodWithDevice, getDetectorParts } from '@/utils/methodHelpers';
  import SetupPostprocessings from '@/components/blocks/postprocessings/vueSetupPostprocessings/SetupPostprocessings.vue';
  import IconMaterial from '@/uikitBase/icons/IconMaterial.vue';
  import { METHOD_RESET_REASONS } from '@/constants/hardware/eventReasons';

  const EVENT_CHANGE_MODEL = 'update:model';
  const isConfigurationForMethodAvailable = (configuration) =>
    configuration != null &&
    (Array.isArray(configuration) || Object.keys(configuration).length > 0);

  export default {
    name: 'DeviceMethodColumnInputs',

    components: {
      IconMaterial,
      SetupPostprocessings,
      TransitionExpand,
      Btn,
      SelectColumnComponent,
      SelectMethodComponent,
      DecoratorInput,
      ApiDeviceSelector,
    },

    model: {
      prop: 'model',
      event: EVENT_CHANGE_MODEL,
    },

    props: {
      model: {
        type: Object,
        required: true,
        default() {
          return {
            device: undefined,
            method: undefined,
            column: undefined,
            postprocessings: {
              D: null,
              D2: null,
              D3: null,
              D4: null,
            },
          };
        },
      },
      isReadonly: {
        type: Boolean,
        default: false,
      },
      isRequired: {
        type: Boolean,
        default: false,
      },
      hideColumn: {
        type: Boolean,
        default: false,
      },
      canSelectMethod: {
        type: Boolean,
        default: true,
      },
      canSelectPostprocessing: {
        type: Boolean,
        default: false,
      },
      canPostprocessMultipleChannels: {
        type: Boolean,
        default: false,
      },
      isRequiredSingleChannelMode: {
        type: Boolean,
        default: false,
      },
      isRequiredPostprocessing: {
        type: Boolean,
        default: false,
      },
      isCalibrationSequenceMode: {
        type: Boolean,
        default: false,
      },
    },

    computed: {
      deviceConfigurationForMethod() {
        const configuration = this.model.device?.configuration?.configuration_for_method;
        return isConfigurationForMethodAvailable(configuration) ? configuration : null;
      },

      methodConfiguration() {
        const configuration = this.model.method?.hardware_configuration;
        return isConfigurationForMethodAvailable(configuration) ? configuration : null;
      },

      deviceConfiguration() {
        return this.deviceConfigurationForMethod ?? this.methodConfiguration;
      },

      showMethodDifferent() {
        return (
          !this.isReadonly &&
          this.model.device?.method &&
          this.model.device?.method?.id !== this.model.method?.id
        );
      },

      showColumnDifferent() {
        return (
          !this.isReadonly &&
          this.model.device?.column &&
          this.model.device?.column?.id !== this.model.column?.id
        );
      },

      isSelectedSingleChannel() {
        return this.model.method?.detector_single_channel != null;
      },

      availableChannels() {
        const channels = Object.keys(getDetectorParts(this.deviceConfiguration?.detector?.[0]));
        return this.isSelectedSingleChannel
          ? channels.filter((channel) => channel === this.model.method?.detector_single_channel)
          : this.canPostprocessMultipleChannels
          ? channels
          : [];
      },

      detectorConfiguration() {
        return this.deviceConfiguration?.detector?.[0];
      },
      detectorPortDescription() {
        return this.detectorConfiguration?.name ?? '';
      },

      isMethodCompatible() {
        if (!this.model.method) {
          return true;
        }

        const currentMethodId = this.model.method?.id;
        const deviceMethodId = this.model.device?.method?.id;

        if (currentMethodId === deviceMethodId) {
          return (
            this.model.device &&
            this.model.device.method_invalidated_reason !==
              METHOD_RESET_REASONS.CONFIGURATION_INCOMPATIBLE
          );
        }

        if (!this.deviceConfigurationForMethod) {
          return true;
        }

        return canUseMethodWithDevice(this.deviceConfigurationForMethod, this.methodConfiguration);
      },
    },

    watch: {
      model: {
        handler() {
          if (
            this.deviceConfigurationForMethod != null &&
            this.methodConfiguration != null &&
            !canUseMethodWithDevice(this.deviceConfigurationForMethod, this.methodConfiguration)
          ) {
            this.setMethod(undefined);
          }
        },
        deep: true,
        immediate: true,
      },
    },

    methods: {
      async getFullMethod(methodId) {
        try {
          return await apiMethods.getMethod(methodId);
        } catch {
          this.notifyError('Unable to get a method!');
        }
      },

      setModel(data) {
        if (this.hideColumn) {
          data.column = undefined;
        }
        this.$emit(EVENT_CHANGE_MODEL, { ...this.model, ...data });
      },

      async setDevice(device) {
        if (this.isReadonly) {
          this.setModel({ device });
        } else {
          this.setModel({
            device,
            method:
              this.model.method ||
              (device.method ? await this.getFullMethod(device.method.id) : null),
            column: this.model.column || device.column,
          });
        }
      },
      setMethod(method) {
        if (method?.detector_single_channel !== this.model.method?.detector_single_channel) {
          const channelMode = method?.detector_single_channel;

          const postprocessings = this.isCalibrationSequenceMode
            ? {
                D: null,
                D2: null,
                D3: null,
                D4: null,
              }
            : channelMode === 'all' || channelMode == null
            ? this.model.postprocessings
            : {
                D: null,
                D2: null,
                D3: null,
                D4: null,
                [channelMode]: this.model.postprocessings[channelMode],
              };

          this.setModel({
            method,
            postprocessings,
          });
        } else {
          this.setModel({
            method,
          });
        }
      },
      setColumn(column) {
        this.setModel({ column });
      },
      setPostprocessings(postprocessings) {
        this.setModel({ postprocessings });
      },

      focus() {
        // stub
      },
    },
  };
</script>

<style lang="scss" scoped>
  .device-method-column-inputs {
    &__loading-devices {
      margin-left: 48px;

      @media (max-width: 500px) {
        margin-left: 10px;
      }
    }

    &__warning-block {
      margin-top: 4px;
      margin-left: 44px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-start;
    }

    &__error-wrong-method {
      color: $color-text-danger;
      margin-top: 5px;
    }

    &__note {
      flex-basis: 100%;
      color: $color-text-third;
    }

    &__message-no-channel {
      color: $color-text-danger;
    }

    &__error-no-detector {
      color: $color-text-danger;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
</style>
